import React from 'react';
import { X, AlertCircle, Mail, Building, Users, ArrowRight, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ValidationError } from '../lib/campaignValidation';

interface CampaignValidationErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ValidationError[];
}

export function CampaignValidationErrorModal({
  isOpen,
  onClose,
  errors,
}: CampaignValidationErrorModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getErrorIcon = (type: ValidationError['type']) => {
    switch (type) {
      case 'sendgrid':
        return Mail;
      case 'business_info':
        return Building;
      case 'subscribers':
        return Users;
      case 'campaign_content':
        return FileText;
      default:
        return AlertCircle;
    }
  };

  const getActionLabel = (type: ValidationError['type']) => {
    switch (type) {
      case 'campaign_content':
        return 'Campagne bewerken';
      case 'subscribers':
        return 'Naar mailinglijst';
      case 'business_info':
      case 'sendgrid':
      default:
        return 'Ga naar instellingen';
    }
  };

  const handleActionClick = (actionLink?: string) => {
    if (actionLink) {
      onClose();
      navigate(actionLink);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#231F20]">
              Campaign kan nog niet verstuurd worden
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-gray-600 mb-4">
            Los de volgende punten op voordat je deze campaign kunt versturen:
          </p>

          <div className="space-y-3">
            {errors.map((error, index) => {
              const Icon = getErrorIcon(error.type);
              return (
                <div
                  key={index}
                  className="border border-red-200 rounded-lg p-4 bg-red-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-red-900 mb-1">
                        {error.message}
                      </h3>
                      {error.action && (
                        <p className="text-sm text-red-800 mb-2">{error.action}</p>
                      )}
                      {error.actionLink && (
                        <button
                          onClick={() => handleActionClick(error.actionLink)}
                          className="inline-flex items-center gap-1 text-sm font-medium text-red-700 hover:text-red-800 transition-colors"
                        >
                          {getActionLabel(error.type)}
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Tip:</span> Zorg dat alle instellingen correct zijn ingevuld voordat je een campaign verstuurt. Dit zorgt voor een professionele uitstraling en voldoet aan wettelijke vereisten.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <button onClick={onClose} className="btn-primary w-full">
            Begrepen
          </button>
        </div>
      </div>
    </div>
  );
}
